import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { service } from '@ember/service';
import { htmlSafe } from '@ember/template';
import type Owner from '@ember/owner';
import type Session from '../services/session';
import type ShareIntentService from '../services/share-intent';
import type OfflineQueue from '../services/offline-queue';
import type NetworkStatus from '../services/network-status';
import type RouterService from '@ember/routing/router-service';
import { Capacitor } from '@capacitor/core';
import Icon from '../components/icon';
import CheckIcon from '~icons/lucide/check-circle-2';
import AlertIcon from '~icons/lucide/alert-circle';
import LoaderIcon from '~icons/lucide/loader-2';
import CloudOffIcon from '~icons/lucide/cloud-off';
import ExternalLinkIcon from '~icons/lucide/external-link';
import { apiFetch } from '../fetch';
import { on } from '@ember/modifier';
import { action } from '@ember/object';

type DrawerState =
  'submitting' | 'success' | 'error' | 'unauthenticated' | 'queued';

interface SubmissionResponse {
  data?: { attributes?: { sourceId?: string | null } };
  errors?: Array<{ detail?: string }>;
}

export default class ShareComponent extends Component {
  @service declare session: Session;
  @service declare shareIntent: ShareIntentService;
  @service declare offlineQueue: OfflineQueue;
  @service declare networkStatus: NetworkStatus;
  @service declare router: RouterService;

  @tracked state: DrawerState = 'submitting';
  @tracked error: string | null = null;
  @tracked isClosing = false;
  @tracked isClosingFromDrag = false;
  @tracked sourceId: string | null = null;
  @tracked dragOffset = 0;

  private touchStartY = 0;
  private savedDragOffset = 0;

  constructor(owner: Owner, args: object) {
    super(owner, args);

    // Make the HTML document transparent so the Android dialog window's
    // native dim-behind overlay shows through to the sharing app.
    document.documentElement.style.background = 'transparent';
    document.body.style.background = 'transparent';

    // Render the drawer immediately with the spinner, then complete initialization
    // asynchronously once session.setup() finishes in the background.
    void this.initialize();
  }

  private async initialize(): Promise<void> {
    // session.setup() runs in parallel with the WebView boot. Await it here so
    // the drawer is already visible while the session round-trip completes.
    await this.shareIntent.sessionSetupPromise;

    if (!this.session.isAuthenticated) {
      this.state = 'unauthenticated';
      return;
    }

    const sharedImage = this.shareIntent.consumeImage();
    if (sharedImage) {
      void this.submitImage(sharedImage.uri, sharedImage.type);
      return;
    }

    const sharedUrl = this.shareIntent.consume();
    if (sharedUrl) {
      void this.submitUrl(sharedUrl);
      return;
    }

    // No intent received (e.g. navigated here manually in browser)
    this.state = 'error';
    this.error = 'No shared content found.';
  }

  private async submitUrl(url: string): Promise<void> {
    // Instant offline check — skip the network entirely if we know we're disconnected.
    if (!this.networkStatus.isOnline) {
      await this.offlineQueue.enqueueUrl(url);
      this.state = 'queued';
      return;
    }

    try {
      const headers: Record<string, string> = {
        'Content-Type': 'application/vnd.api+json',
      };
      const authHeader = this.session.authHeader;
      if (authHeader) headers['Authorization'] = authHeader;

      const response = await apiFetch('/api/submissions', {
        method: 'POST',
        headers,
        body: JSON.stringify({ url }),
      });

      const data = (await response.json()) as SubmissionResponse;
      if (!response.ok) {
        throw new Error(data.errors?.[0]?.detail ?? 'Submission failed');
      }

      this.sourceId = data.data?.attributes?.sourceId ?? null;
      this.state = 'success';
    } catch {
      // API unreachable or error — queue for later sync
      await this.offlineQueue.enqueueUrl(url);
      this.state = 'queued';
    }
  }

  private async submitImage(uri: string, mimeType: string): Promise<void> {
    // Phase 1: Read the blob from the local content:// URI immediately — this
    // works offline and keeps the blob in scope for queuing without a re-fetch.
    let blob: Blob;
    try {
      const fetchUri = Capacitor.isNativePlatform()
        ? Capacitor.convertFileSrc(uri)
        : uri;
      // eslint-disable-next-line warp-drive/no-external-request-patterns -- native content:// URI via Capacitor bridge
      const imgResponse = await fetch(fetchUri);
      if (!imgResponse.ok) {
        throw new Error(`Failed to read shared image (${imgResponse.status})`);
      }
      blob = await imgResponse.blob();
    } catch (err) {
      this.state = 'error';
      this.error =
        err instanceof Error ? err.message : 'Failed to read shared image';
      return;
    }

    // Phase 2: Instant offline check — skip the network call if we're disconnected.
    if (!this.networkStatus.isOnline) {
      await this.offlineQueue.enqueueImage(blob, mimeType);
      this.state = 'queued';
      return;
    }

    // Phase 3: Try the API upload; fall back to offline queue on any failure.
    try {
      const ext = mimeType.split('/')[1] ?? 'jpg';
      const file = new File([blob], `shared-image.${ext}`, { type: mimeType });

      const formData = new FormData();
      formData.append('image', file);

      const headers: Record<string, string> = {};
      const authHeader = this.session.authHeader;
      if (authHeader) headers['Authorization'] = authHeader;

      const response = await apiFetch('/api/submissions/image', {
        method: 'POST',
        headers,
        body: formData,
      });

      const data = (await response.json()) as SubmissionResponse;
      if (!response.ok) {
        throw new Error(data.errors?.[0]?.detail ?? 'Failed to upload image');
      }

      this.sourceId = data.data?.attributes?.sourceId ?? null;
      this.state = 'success';
    } catch {
      // API unreachable or error — queue the already-read blob for later sync.
      await this.offlineQueue.enqueueImage(blob, mimeType);
      this.state = 'queued';
    }
  }

  @action
  async dismiss(): Promise<void> {
    try {
      if (Capacitor.isNativePlatform()) {
        const { SendIntent } = await import('@mindlib-capacitor/send-intent');
        SendIntent.finish();
      }
    } catch {
      // Not native or plugin unavailable — no-op
    }
  }

  @action
  startDismiss(): void {
    if (this.isClosing || this.isClosingFromDrag) return;
    if (this.dragOffset > 0) {
      this.savedDragOffset = this.dragOffset;
      this.dragOffset = 0;
      this.isClosingFromDrag = true;
    } else {
      this.isClosing = true;
    }
    setTimeout(() => {
      this.isClosing = false;
      this.isClosingFromDrag = false;
      this.savedDragOffset = 0;
      void this.dismiss();
    }, 250);
  }

  @action
  handleBackdropClick(): void {
    // Block accidental dismiss while a save is in flight
    if (this.state === 'submitting') return;
    this.startDismiss();
  }

  @action
  async openSource(): Promise<void> {
    if (!this.sourceId) return;
    // Restore backgrounds — navigating into the main app chrome
    document.documentElement.style.background = '';
    document.body.style.background = '';

    if (Capacitor.isNativePlatform()) {
      // ShareActivity is a separate floating WebView — router.transitionTo() here
      // only navigates inside the overlay, not MainActivity.
      //
      // Instead: fire the `saive://` custom scheme via an anchor click.
      // Capacitor's BridgeWebViewClient intercepts non-http(s) schemes and calls
      // startActivity(Intent.ACTION_VIEW, FLAG_ACTIVITY_NEW_TASK), which brings
      // MainActivity to the foreground (launchMode=singleTask → onNewIntent).
      // The appUrlOpen event in deep-links.ts then calls router.transitionTo().
      //
      // localStorage stores a fallback: if appUrlOpen doesn't fire (e.g. dev
      // build), the appStateChange listener in deep-links.ts picks it up when
      // the user manually reopens the app.
      const path = `/sources/${this.sourceId}`;
      localStorage.setItem('favoris:open-after-share', path);
      const a = document.createElement('a');
      a.href = `saive://open${path}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } else {
      this.router.transitionTo('sources.show', this.sourceId);
    }

    await this.dismiss();
  }

  // --- Drag-to-close ---

  @action
  onTouchStart(event: TouchEvent): void {
    // Don't allow dragging during an active save
    if (this.state === 'submitting') return;
    if (event.touches.length === 0) return;
    this.touchStartY = event.touches[0]!.clientY;
    this.dragOffset = 0;
  }

  @action
  onTouchMove(event: TouchEvent): void {
    if (this.state === 'submitting') return;
    if (event.touches.length === 0) return;
    const delta = event.touches[0]!.clientY - this.touchStartY;
    this.dragOffset = Math.max(0, delta);
  }

  @action
  onTouchEnd(): void {
    if (this.dragOffset > 80) {
      this.startDismiss();
    } else {
      this.dragOffset = 0;
    }
  }

  // --- Style/class helpers ---

  get panelStyle() {
    // Closing from drag: tell the @keyframes where to start via a CSS custom property
    if (this.isClosingFromDrag) {
      return htmlSafe(`--drawer-drag-start: ${this.savedDragOffset}px;`);
    }
    // Actively dragging: follow the finger
    if (this.dragOffset > 0) {
      return htmlSafe(`transform: translateY(${this.dragOffset}px);`);
    }
    return htmlSafe('');
  }

  get panelClass(): string {
    const base =
      'absolute bottom-0 left-0 right-0 bg-base-100 rounded-t-2xl p-6 pb-10 shadow-2xl';
    if (this.dragOffset > 0) return base;
    if (this.isClosingFromDrag) return `${base} animate-drawer-out-from-drag`;
    if (this.isClosing) return `${base} animate-drawer-out`;
    return `${base} animate-drawer-in`;
  }

  get backdropClass(): string {
    const base = 'absolute inset-0 bg-black/50';
    if (this.isClosing || this.isClosingFromDrag)
      return `${base} animate-backdrop-out`;
    return `${base} animate-backdrop-in`;
  }

  <template>
    {{! Full-viewport backdrop — click outside to dismiss }}
    <div class="fixed inset-0" data-test-share-drawer>
      {{! Scrim as a real button so the panel's buttons don't violate a11y interactive nesting rules }}
      <button
        type="button"
        class="cursor-default {{this.backdropClass}}"
        aria-label="Close"
        {{on "click" this.handleBackdropClick}}
      ></button>

      {{! Drawer panel — slides up on open, slides down on close, follows finger on drag }}
      {{! template-lint-disable no-invalid-interactive }}
      <div
        role="dialog"
        aria-modal="true"
        class={{this.panelClass}}
        style={{this.panelStyle}}
        {{on "touchstart" this.onTouchStart}}
        {{on "touchmove" this.onTouchMove}}
        {{on "touchend" this.onTouchEnd}}
      >
        {{! template-lint-enable no-invalid-interactive }}
        {{! Drag handle — visual affordance }}
        <div class="flex touch-none justify-center pb-4">
          <div class="bg-base-300 h-1.5 w-12 rounded-full"></div>
        </div>

        {{#if (isSubmitting this.state)}}
          <div class="flex flex-col items-center gap-4 py-4">
            <Icon
              @icon={{LoaderIcon}}
              class="text-primary h-10 w-10 animate-spin"
            />
            <p class="text-base-content text-base font-medium">Saving…</p>
          </div>
        {{else if (isSuccess this.state)}}
          <div class="flex flex-col items-center gap-4 py-4">
            <Icon @icon={{CheckIcon}} class="text-success h-10 w-10" />
            <p class="text-base-content text-base font-semibold">Saved!</p>
            <p class="text-base-content/70 text-center text-sm">
              Open Favoris to see your results when the extraction is complete.
            </p>
            {{#if this.sourceId}}
              <button
                type="button"
                class="btn btn-outline mt-2 w-full"
                {{on "click" this.openSource}}
              >
                <Icon @icon={{ExternalLinkIcon}} class="h-4 w-4" />
                Open in Favoris
              </button>
            {{/if}}
            <button
              type="button"
              class="btn btn-primary w-full"
              {{on "click" this.startDismiss}}
            >
              Done
            </button>
          </div>
        {{else if (isQueued this.state)}}
          <div class="flex flex-col items-center gap-4 py-4">
            <Icon @icon={{CloudOffIcon}} class="text-info h-10 w-10" />
            <p class="text-base-content text-base font-semibold">Saved offline</p>
            <p class="text-base-content/70 text-center text-sm">
              Will sync automatically when you're back online.
            </p>
            <button
              type="button"
              class="btn btn-primary mt-2 w-full"
              {{on "click" this.startDismiss}}
            >
              Done
            </button>
          </div>
        {{else if (isUnauthenticated this.state)}}
          <div class="flex flex-col items-center gap-4 py-4">
            <Icon @icon={{AlertIcon}} class="text-warning h-10 w-10" />
            <p class="text-base-content text-base font-semibold">Not logged in</p>
            <p class="text-base-content/70 text-center text-sm">
              Open Favoris and log in, then try sharing again.
            </p>
            <button
              type="button"
              class="btn btn-outline mt-2 w-full"
              {{on "click" this.startDismiss}}
            >
              Dismiss
            </button>
          </div>
        {{else}}
          <div class="flex flex-col items-center gap-4 py-4">
            <Icon @icon={{AlertIcon}} class="text-error h-10 w-10" />
            <p class="text-base-content text-base font-semibold">Something went
              wrong</p>
            {{#if this.error}}
              <p class="text-error text-center text-sm">{{this.error}}</p>
            {{/if}}
            <button
              type="button"
              class="btn btn-outline mt-2 w-full"
              {{on "click" this.startDismiss}}
            >
              Dismiss
            </button>
          </div>
        {{/if}}
      </div>
    </div>
  </template>
}

function isSubmitting(state: DrawerState): boolean {
  return state === 'submitting';
}

function isSuccess(state: DrawerState): boolean {
  return state === 'success';
}

function isUnauthenticated(state: DrawerState): boolean {
  return state === 'unauthenticated';
}

function isQueued(state: DrawerState): boolean {
  return state === 'queued';
}
